'use client'

import * as React from 'react'
import { Dialog as DialogPrimitive } from '@base-ui/react/dialog'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { PortalContainerContext } from '@/components/ui/portal-container-context'
import { stopReactBubble } from '@/components/ui/stop-react-bubble'
import { XIcon } from 'lucide-react'
import {
  DIALOG_OVERLAY_CLASS,
  DIALOG_POPUP_CLASS,
  DIALOG_FRAME_CLASS,
  DIALOG_VIEWPORT_CLASS,
  markModalViewport,
  DialogEnterContext,
  continueEnter,
} from '@/components/ui/dialog-surface'

function Dialog({ ...props }: DialogPrimitive.Root.Props) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />
}

function DialogTrigger({ ...props }: DialogPrimitive.Trigger.Props) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

function DialogPortal({ ...props }: DialogPrimitive.Portal.Props) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
}

function DialogClose({ ...props }: DialogPrimitive.Close.Props) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />
}

function DialogOverlay({ className, onClick, ...props }: DialogPrimitive.Backdrop.Props) {
  return (
    <DialogPrimitive.Backdrop
      data-slot="dialog-overlay"
      onClick={stopReactBubble(onClick)}
      className={cn(DIALOG_OVERLAY_CLASS, className)}
      {...props}
    />
  )
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  forceBackdrop = false,
  nested = false,
  frameClassName,
  onClick,
  ...props
}: DialogPrimitive.Popup.Props & {
  showCloseButton?: boolean
  /** The frame's own classes (`DIALOG_FRAME_CLASS`), for a dialog that drops its gutter (full-screen on a phone). */
  frameClassName?: string
  /**
   * Render the dim/blur even when this dialog is NESTED (base-ui drops a nested backdrop so dims do not stack).
   * Needed when the parent stays fully visible behind it — the mobile user-menu sheet, whose own backdrop steps
   * aside in drawer.tsx so only one blur is ever on screen.
   */
  forceBackdrop?: boolean
  /**
   * A FORM dialog opened on top of another form's dialog: without a backdrop of its own the two sets of fields
   * land on one flat surface and read as a single long form.
   */
  nested?: boolean
}) {
  const [portalContainer, setPortalContainer] = React.useState<HTMLElement | null>(null)
  const closeButtonRef = React.useRef<HTMLButtonElement>(null)
  const backdropRef = React.useRef<HTMLDivElement>(null)
  const handover = React.use(DialogEnterContext)
  // The backdrop comes first in the portal, so its ref is set by the time the popup's is.
  const popupRef = (popup: HTMLDivElement | null) => {
    setPortalContainer(popup)
    if (popup) continueEnter(handover, [backdropRef.current, popup])
  }
  return (
    <DialogPortal>
      <DialogOverlay
        ref={backdropRef}
        forceRender={forceBackdrop || nested}
        className={cn(nested && 'bg-black/50 supports-backdrop-filter:backdrop-blur-sm')}
      />
      <DialogPrimitive.Viewport
        data-slot="dialog-viewport"
        ref={markModalViewport}
        onClick={stopReactBubble()}
        className={DIALOG_VIEWPORT_CLASS}
      >
        <div data-slot="dialog-frame" className={cn(DIALOG_FRAME_CLASS, frameClassName)}>
          <DialogPrimitive.Popup
            ref={popupRef}
            data-slot="dialog-content"
            onClick={stopReactBubble(onClick)}
            initialFocus={showCloseButton ? closeButtonRef : undefined}
            className={cn(
              DIALOG_POPUP_CLASS,
              '*:data-[base-ui-portal]:contents',
              nested && 'ring-foreground/25 shadow-2xl',
              className,
            )}
            {...props}
          >
            <PortalContainerContext.Provider value={portalContainer}>
              {/* A dialog opened from inside this one plays its own enter. */}
              <DialogEnterContext value={null}>{children}</DialogEnterContext>
            </PortalContainerContext.Provider>
            {showCloseButton && (
              <DialogPrimitive.Close
                data-slot="dialog-close"
                render={
                  <Button ref={closeButtonRef} variant="ghost" className="absolute top-2 right-2 z-20" size="icon-sm" />
                }
              >
                <XIcon />
                <span className="sr-only">Close</span>
              </DialogPrimitive.Close>
            )}
          </DialogPrimitive.Popup>
        </div>
      </DialogPrimitive.Viewport>
    </DialogPortal>
  )
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="dialog-header" className={cn('flex flex-col gap-2', className)} {...props} />
}

function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<'div'> & {
  showCloseButton?: boolean
}) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn('-mx-4 -mb-4 flex flex-col-reverse gap-2 rounded-b-xl p-4 sm:flex-row sm:justify-end', className)}
      {...props}
    >
      {children}
      {showCloseButton && <DialogPrimitive.Close render={<Button variant="outline" />}>Close</DialogPrimitive.Close>}
    </div>
  )
}

function DialogTitle({ className, ...props }: DialogPrimitive.Title.Props) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn('text-base leading-none font-medium', className)}
      {...props}
    />
  )
}

function DialogDescription({ className, ...props }: DialogPrimitive.Description.Props) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn(
        'text-sm text-muted-foreground *:[a]:underline *:[a]:underline-offset-3 *:[a]:hover:text-foreground',
        className,
      )}
      {...props}
    />
  )
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
}
